<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, ref, toRef, watch } from 'vue'

import { useGetLinkClicksCpc, useRecalculateLinkClicksCpc } from '@/api/link-clicks.api'
import LinkIcon from '@/assets/icons/link.svg?component'
import { serializeLinkClicksCpcPayload } from '@/lib/adapters/link-clicks-cpc.adapter'
import { notification } from '@/lib/helpers'
import { useTrafficSourcesStore } from '@/stores/traffic-sources'
import { ButtonComponent, FlexComponent, InputComponent, LoadingComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  linkId: number
}>()

const tagIdRef = toRef(props, 'linkId')
const { items, isLoading, refresh } = useGetLinkClicksCpc(tagIdRef)
const { recalculate, isLoading: isRecalculating } = useRecalculateLinkClicksCpc()

const trafficSourcesStore = useTrafficSourcesStore()
const { data: trafficSources } = storeToRefs(trafficSourcesStore)

const sourceNameMap = computed(() => Object.fromEntries(trafficSources.value.map((s) => [s.source, s.name])))

const newCpcValues = ref<number[]>([])

watch(
  items,
  (val) => {
    newCpcValues.value = val.map((item) => item.userRevenueCpc)
  },
  { immediate: true },
)

const onRecalculate = async () => {
  const success = await recalculate(props.linkId, serializeLinkClicksCpcPayload(items.value, newCpcValues.value))
  if (!success) {
    return
  }
  await refresh()
  notification.success('Link clicks updated. User summary tables will be updated in a 1-2 min.')
}
</script>

<template>
  <LoadingComponent v-if="isLoading" :size="48" />

  <TypographyComponent v-else-if="items.length === 0" variant="body-large">No clicks found</TypographyComponent>

  <FlexComponent v-else direction="column" :gap="32">
    <FlexComponent direction="column" :gap="8">
      <FlexComponent class="cpc-head">
        <TypographyComponent variant="title-large" class="cpc-head__source">Traffic source</TypographyComponent>
        <TypographyComponent variant="title-large" class="cpc-head__campaign">Campaign ID</TypographyComponent>
        <TypographyComponent variant="title-large" class="cpc-head__cpc">CPC</TypographyComponent>
        <TypographyComponent variant="title-large" class="cpc-head__clicks">Clicks</TypographyComponent>
      </FlexComponent>

      <FlexComponent v-for="(item, i) in items" :key="i" align="center" :gap="16" class="cpc-row">
        <FlexComponent align="center" :gap="8" class="cpc-row__source">
          <el-icon :size="24">
            <LinkIcon />
          </el-icon>
          <TypographyComponent variant="body-large">{{ sourceNameMap[item.source] ?? `Source ${item.source}` }}</TypographyComponent>
        </FlexComponent>

        <div class="cpc-row__campaign">{{ item.offerIds?.[0] ?? '—' }}</div>

        <div class="cpc-row__cpc">
          <InputComponent v-model="newCpcValues[i]" type="number">
            <template #suffix>
              <TypographyComponent variant="title-large">{{ item.currency }}</TypographyComponent>
            </template>
          </InputComponent>
        </div>

        <div class="cpc-row__clicks">{{ item.count }}</div>
      </FlexComponent>
    </FlexComponent>

    <FlexComponent justify="flex-end">
      <ButtonComponent variant="primary" :is-loading="isRecalculating" @click="onRecalculate">Recalculate</ButtonComponent>
    </FlexComponent>
  </FlexComponent>
</template>

<style scoped lang="scss">
.cpc-head {
  padding: 0 16px 8px;
  align-items: center;
  gap: 16px;

  &__source {
    width: 160px;
    flex-shrink: 0;
  }

  &__campaign {
    width: 120px;
    flex-shrink: 0;
  }

  &__cpc {
    flex: 1;
  }

  &__clicks {
    width: 96px;
    flex-shrink: 0;
    text-align: right;
  }
}

.cpc-row {
  padding: 16px;
  border: 1px solid rgba(34, 34, 34, 0.08);
  border-radius: 16px;

  &__source {
    width: 160px;
    flex-shrink: 0;
  }

  &__campaign {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 120px;
    height: 56px;
    flex-shrink: 0;
    font-size: 16px;
    line-height: 24px;
  }

  &__cpc {
    flex: 1;
  }

  &__clicks {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 96px;
    height: 56px;
    background: rgba(34, 34, 34, 0.02);
    border: 1px solid rgba(34, 34, 34, 0.12);
    border-radius: 8px;
    font-size: 16px;
    line-height: 24px;
    text-align: center;
  }
}
</style>
